import { Suspense, useEffect, useState } from "react";
import "./App.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";

import { ErrorBoundary } from "@components/ErrorBoundary";
import { NyleConfig, NyleConfigProvider } from "@components/NyleConfig";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import { useLocation, useRoutes } from "react-router-dom";
import { routes } from "./router";
import { useCheckSession } from "@hooks/useCheckSession";
import { Spinner } from "@components/ui-kit/Spinner";
import { Chat } from "@components/ChatBot";
import { RouteTracker } from "@components/Analytics/RouteTracker";
import { BackgroundProvider } from "@components/BackfroundContext";
import posthog from "posthog-js";
import { POSTHOG_API_KEY } from "@lib/env";
import { subscribeToPush } from "./utils/notifications";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { featureToggleManager } from "@lib/featureToggles";
import { FeatureToggleDebugTrigger } from "@components/FeatureToggleDebugTrigger";
import { useFeatureToggleManager } from "@hooks/useFeatureToggle";
import { useUserStore } from "./store/user.state";
import { useAuthStore } from "./store/auth.state";
import { getUserIdFromToken } from "./utils/auth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

posthog.init(POSTHOG_API_KEY || "", {
  api_host: "https://us.i.posthog.com",
  person_profiles: "identified_only",
});

function App() {
  const router = useRoutes(routes);
  const sessionLoading = useCheckSession();
  const location = useLocation();
  const { isInitialized } = useFeatureToggleManager();
  const [userId, setUserId] = useState<string | null>(null);

  const { user } = useUserStore();
  const { authenticated } = useAuthStore();

  // Get user ID from authenticated user data
  useEffect(() => {
    const getUserId = (): string | null => {
      // First priority: Use user UUID from user store if available
      if (user?.uuid) {
        return user.uuid;
      }

      // Second priority: Extract user ID from JWT token
      if (authenticated) {
        const tokenUserId = getUserIdFromToken();
        if (tokenUserId) {
          return tokenUserId;
        }
      }

      // Fallback for unauthenticated users
      return "anonymous-user";
    };

    setUserId(getUserId());
  }, [user, authenticated]);

  // Ensure PostHog identification when user data is available
  useEffect(() => {
    if (user?.uuid && user?.email) {
      try {
        posthog.identify(user.uuid, {
          email: user.email,
          firstName: user.first_name || "",
          lastName: user.last_name || "",
        });
      } catch (error) {
        console.warn("Failed to identify user in PostHog:", error);
      }
    }
  }, [user?.uuid, user?.email]);

  // Initialize feature toggles with user identification
  useEffect(() => {
    if (userId) {
      // Initialize feature toggles with user identification (prevents UI flicker)
      featureToggleManager.initialize(userId).catch((error) => {
        console.error("Failed to initialize feature toggles:", error);
        // Don't block the app if feature toggles fail to initialize
      });
    }
  }, [userId]);

  useEffect(() => {
    subscribeToPush().catch(console.error);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Add a timeout for feature toggle initialization to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isInitialized) {
        console.warn(
          "Feature toggles taking too long to initialize, proceeding without them",
        );
        // Force initialization if it's taking too long
        featureToggleManager.forceInitializeWithDefaults();
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [isInitialized]);

  const loading = sessionLoading;

  return (
    <ErrorBoundary>
      <ResponsiveProvider>
        <BackgroundProvider>
          <NyleConfigProvider>
            <div className="app">
              <NyleConfig />
              <ToastContainer position="bottom-right" autoClose={4000} />
              <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools
                  initialIsOpen={false}
                  position="bottom-right"
                />
                <Suspense fallback="">
                  {loading ? (
                    <Spinner size="xxl" className="app__spinner" />
                  ) : (
                    <>
                      <RouteTracker />
                      {router}
                      <Chat />
                    </>
                  )}
                  {/* Debug trigger should always be available for debugging */}
                  <FeatureToggleDebugTrigger />
                </Suspense>
              </QueryClientProvider>
            </div>
          </NyleConfigProvider>
        </BackgroundProvider>
      </ResponsiveProvider>
    </ErrorBoundary>
  );
}

export default App;
